import {AsyncState, mapAsyncState} from "src/utils/async/asyncState.ts";
import {ControlledAsyncCrudDataGridProps} from "./crud/ControlledAsyncCrudDataGrid";
import {useState} from "react";
import {useParameterizedAsyncData} from "src/utils/async/parameterizedAsyncData.ts";
import {GridRowId} from "@mui/x-data-grid-pro";

export interface UseAsyncDataGridModelParams<TParams extends object, TResult, TRow extends object> {
	fetchData: (params: TParams) => Promise<TResult>;
	getRows: (result: TResult) => TRow[];
	getRowId: (row: TRow) => GridRowId;
	initialParams: TParams;
	onRowSelectionChanged?: (newSelection: TRow[]) => void;
}

export interface UseAsyncDataGridModelReturn<TParams extends object, TResult, TRow extends object> {
	dataAsync: AsyncState<TResult>;
	paramValues: TParams;
	refresh: (newParams?: Partial<TParams>) => Promise<unknown>;
	selectedRows: TRow[];
	onlySelectedRow: TRow | undefined;
	dataGridProps: DataGridModelDataGridProps<TRow>;
}

export type DataGridModelDataGridProps<TRow extends object> = Required<
	Pick<
		ControlledAsyncCrudDataGridProps<TRow>,
		"rowsAsync" | "refreshData" | "onRowSelectionChanged" | "getRowId"
	>
>;

export const useAsyncDataGridModel = <TParams extends object, TResult, TRow extends object>({
	fetchData,
	getRows,
	getRowId,
	initialParams,
	onRowSelectionChanged,
}: UseAsyncDataGridModelParams<TParams, TResult, TRow>): UseAsyncDataGridModelReturn<
	TParams,
	TResult,
	TRow
> => {
	const [selectedRows, setSelectedRows] = useState<TRow[]>([]);
	const { dataAsync, paramValues, refresh } = useParameterizedAsyncData(fetchData, initialParams);

	const refreshWrapped = async (newParams?: Partial<TParams>) => {
		const result = await refresh(newParams);
		const selectedRowIds = selectedRows.map(getRowId);
		const newSelectedRows = getRows(result).filter((row) =>
			selectedRowIds.includes(getRowId(row)),
		);
		setSelectedRows(newSelectedRows);
		onRowSelectionChanged?.(newSelectedRows);
	};

	return {
		dataAsync,
		paramValues,
		refresh: refreshWrapped,
		selectedRows,
		onlySelectedRow: selectedRows.length === 1 ? selectedRows[0] : undefined,
		dataGridProps: {
			rowsAsync: mapAsyncState(dataAsync, getRows),
			getRowId: getRowId,
			refreshData: async () => {
				await refresh();
			},
			onRowSelectionChanged: (newSelection) => {
				setSelectedRows(newSelection);
				onRowSelectionChanged?.(newSelection);
			},
		},
	};
};
